/** @jsxRuntime classic */
/** @jsx jsx */
import { Fragment, useState } from 'react'
import { Formik } from 'formik'
import { jsx, Text } from 'theme-ui'
import { Transition } from 'react-transition-group'

import {
  ContactFormBoxStyled,
  ContactFormStyled,
  ContactFormButtonStyled,
  ContactFormHeadStyled,
  ContactFormInnerStyled,
  ContactFormMsgStyled,
  ContactFormMsgOverlayStyled,
  ContactFormMsgModalStyled,
  ContactFormMsgModalWrapperStyled
} from './styles'
import Button from './../../atoms/Button'
import { ContactFormProps } from './types'
import { Fields } from '../../../enums/fields'
import ContactField from '../../molecules/ContactField'
import FormMessage from '../../molecules/FormMessage'
import { modalMessageTransition, modalMessageContentTransition } from './cfg'
import { useMemo } from 'react'
import * as yup from 'yup'
import { initialValues } from './consts'
import { useEffect } from 'react'
import { isValidPhoneNumber } from 'react-phone-number-input'

const ContactForm: React.FC<ContactFormProps> = ({ head, fields, message, disabled, onSubmit }) => {
  const [isMessageVisible, setIsMessageVisible] = useState(false)

  const messageModalHandler = () => {
    setIsMessageVisible(!isMessageVisible)
  }

  const validationSchema = useMemo(() => {
    const schema = {
      firstName: yup.string(),
      lastName: yup.string(),
      company: yup.string(),
      email: yup.string(),
      phoneNumber: yup.string(),
      country: yup.string(),
      message: yup.string()
    }

    fields.forEach(({ required, fieldName, validationMessage }) => {
      if (required && fieldName in schema) {
        if (fieldName === 'email') {
          schema[fieldName] = schema[fieldName].email(validationMessage).required(validationMessage)
        } else if (fieldName === 'phoneNumber') {
          schema[fieldName] = schema[fieldName].test('phone', validationMessage || '', isValidPhoneNumber)
        } else {
          schema[fieldName] = schema[fieldName].required(validationMessage)
        }
      }
    })

    return yup.object().shape(schema)
  }, [fields])

  useEffect(() => {
    setIsMessageVisible(!!message)
  }, [message])

  return (
    <ContactFormBoxStyled>
      {isMessageVisible && (
        <Transition in={isMessageVisible} timeout={100} appear>
          {(state) => (
            <ContactFormMsgStyled>
              <ContactFormMsgOverlayStyled
                style={{
                  ...modalMessageTransition[state]
                }}
              />
              <ContactFormMsgModalStyled>
                <ContactFormMsgModalWrapperStyled
                  onClick={messageModalHandler}
                  style={{
                    ...modalMessageContentTransition[state]
                  }}
                >
                  <FormMessage clickHandler={messageModalHandler} stopPropagation={(e: any) => e.stopPropagation()} {...message} />
                </ContactFormMsgModalWrapperStyled>
              </ContactFormMsgModalStyled>
            </ContactFormMsgStyled>
          )}
        </Transition>
      )}
      <ContactFormInnerStyled>
        {head && (
          <ContactFormHeadStyled>
            <Text
              as="h2"
              sx={{
                variant: 'headingRaleway7',
                textAlign: 'center',
                fontWeight: 'bold'
              }}
            >
              {head}
            </Text>
          </ContactFormHeadStyled>
        )}
        <Formik
          {...{
            initialValues,
            validationSchema,
            onSubmit
          }}
        >
          {({ values, handleSubmit }) => (
            <ContactFormStyled onSubmit={handleSubmit} autoComplete="off">
              {fields.map((item) => {
                const { fieldName, label, items, hidden } = item

                return (
                  <Fragment key={`form-field-${fieldName}`}>
                    {fieldName === 'firstName' && !hidden && (
                      <ContactField isHalf value={fieldName} label={label} variant={Fields.Text} isActive={values.firstName.length > 0} />
                    )}

                    {fieldName === 'lastName' && !hidden && (
                      <ContactField isHalf value={fieldName} label={label} variant={Fields.Text} isActive={values.lastName.length > 0} />
                    )}

                    {fieldName === 'company' && !hidden && (
                      <ContactField value={fieldName} label={label} variant={Fields.Text} isActive={values.company.length > 0} />
                    )}

                    {fieldName === 'email' && !hidden && (
                      <ContactField value={fieldName} label={label} variant={Fields.Email} isActive={values.email.length > 0} />
                    )}

                    {fieldName === 'phoneNumber' && !hidden && (
                      <ContactField
                        value={fieldName}
                        label={label}
                        variant={Fields.Phone}
                        isActive={values.phoneNumber?.length > 0}
                        isPhone
                      />
                    )}

                    {fieldName === 'country' && !hidden && (
                      <ContactField
                        value={fieldName}
                        label={label}
                        variant={Fields.Select}
                        items={items}
                        isActive={values.country.length > 0}
                      />
                    )}

                    {fieldName === 'message' && !hidden && (
                      <ContactField value={fieldName} label={label} variant={Fields.Message} isActive={values.message.length > 0} />
                    )}
                  </Fragment>
                )
              })}

              <ContactFormButtonStyled type="submit" disabled={disabled}>
                <Button variant="withArrow" isPlain={true} urlText="Submit" />
              </ContactFormButtonStyled>
            </ContactFormStyled>
          )}
        </Formik>
      </ContactFormInnerStyled>
    </ContactFormBoxStyled>
  )
}

export default ContactForm
